import { useEffect, useState } from 'react';

// The purchase-success icon (design_handoff_2/Website Modals.dc.html, "1b").
// A red dot idles at centre, then every ~4.2s "visits" one of three looks —
// a radiating spark burst, a waveform riding one of 4 shapes, or a knob dial
// turning — chosen at random so the icon stays alive without looping the same
// beat. `key={n}` on the <svg> remounts it each visit so every CSS animation
// restarts cleanly (including the entrance pop), rather than needing the
// duration-jitter trick the original used to force a restart in place.

type Mode = 'spark' | 'wave' | 'dial';

// idx picks a burst/shape variant; VISIT_MS is how long a visit stays before
// the next one is rolled.
const VISIT_MS = 4200;

const SPARK_ANIM = ['animate-sparkout', 'animate-sparkspin', 'animate-sparkpulse', 'animate-sparkshimmer'];
const DOT_ANIM = ['animate-popdot', 'animate-wobbledot', 'animate-popdotb', 'animate-wobbledotb'];

const WAVE_PATHS = [
    'M6 30 C 13 17, 22 17, 29 30 S 43 43, 50 30 S 60 21, 66 27', // sine
    'M6 36 L20 22 L20 36 L34 22 L34 36 L48 22 L48 36 L62 22', // sawtooth
    'M6 24 L16 24 L16 36 L26 36 L26 24 L36 24 L36 36 L46 36 L46 24 L56 24 L56 36 L66 36', // square
    'M6 30 L16 20 L26 40 L36 20 L46 40 L56 20 L66 30', // triangle
];

// The 12 lines radiating from the centre dot for the spark burst.
const SPARK_LINES: [number, number, number, number][] = [
    [45, 30, 56, 30],
    [43.8, 34.5, 48.1, 37],
    [40.5, 37.8, 46, 47.3],
    [36, 39, 36, 44],
    [31.5, 37.8, 26, 47.3],
    [28.2, 34.5, 23.9, 37],
    [27, 30, 16, 30],
    [28.2, 25.5, 23.9, 23],
    [31.5, 22.2, 26, 12.7],
    [36, 21, 36, 16],
    [40.5, 22.2, 46, 12.7],
    [43.8, 25.5, 48.1, 23],
];

function rollVisit(prevMode: Mode, prevIdx: number): { mode: Mode; idx: number } {
    const r = Math.random();
    const mode: Mode = r < 0.4 ? 'spark' : r < 0.72 ? 'wave' : 'dial';
    let idx = Math.floor(Math.random() * 4);
    if (mode === prevMode && idx === prevIdx) idx = (idx + 1) % 4;
    return { mode, idx };
}

export function PurchaseSuccessMark() {
    const [{ mode, idx, n }, setVisit] = useState<{ mode: Mode; idx: number; n: number }>({
        mode: 'spark',
        idx: 0,
        n: 0,
    });

    useEffect(() => {
        const id = setInterval(() => {
            setVisit((prev) => ({ ...rollVisit(prev.mode, prev.idx), n: prev.n + 1 }));
        }, VISIT_MS);
        return () => clearInterval(id);
    }, []);

    const wavePath = WAVE_PATHS[idx];

    return (
        <svg key={n} width="72" height="56" viewBox="0 0 72 56" className="animate-badgein" aria-hidden>
            <g
                stroke="rgba(41, 41, 39, 0.55)"
                strokeWidth={0.9}
                style={{ transformOrigin: '36px 30px' }}
                className={mode === 'spark' ? SPARK_ANIM[idx] : 'opacity-0'}
            >
                {SPARK_LINES.map(([x1, y1, x2, y2], i) => (
                    <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
                ))}
            </g>
            <circle
                cx={36}
                cy={30}
                r={5}
                style={{ transformOrigin: '36px 30px', transition: 'opacity 0.45s ease' }}
                className={mode === 'spark' ? `fill-alert opacity-100 ${DOT_ANIM[idx]}` : 'fill-alert opacity-0'}
            />
            <g className={mode === 'wave' ? 'animate-iconvisit' : 'opacity-0'}>
                <path d={wavePath} stroke="rgba(41, 41, 39, 0.4)" fill="none" strokeWidth={0.8} />
                <circle
                    r={3.2}
                    className={mode === 'wave' ? 'fill-alert animate-waveride' : 'fill-alert'}
                    style={{ offsetPath: `path('${wavePath}')` }}
                />
            </g>
            <g className={mode === 'dial' ? 'animate-iconvisit' : 'opacity-0'}>
                <circle cx={36} cy={30} r={10} fill="none" className="stroke-alert" strokeWidth={0.9} />
                <line
                    x1={36}
                    y1={30}
                    x2={30.3}
                    y2={35.7}
                    strokeWidth={1.4}
                    strokeLinecap="round"
                    style={{ transformOrigin: '36px 30px' }}
                    className={mode === 'dial' ? 'stroke-alert animate-dialturn' : 'stroke-alert'}
                />
            </g>
        </svg>
    );
}

export default PurchaseSuccessMark;
